import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthModuleService } from './auth-module.service';

interface CacheEntry<T = unknown> {
  data: T;
  expiresAt: number;
}

const DEFAULT_TTL = 5 * 60 * 1000;
const MAX_ENTRIES = 200;

/**
 * Cache HTTP em memória, chaveado por path+query. Não persiste e é por
 * instância (por request no SSR). Segue o padrão de
 * docs/specs/done/limpeza-de-sessao-no-logout.md: assina sessionCleared$ e
 * zera tudo no próprio resetSession() (dados de A não podem vazar pra B).
 */
@Injectable({ providedIn: 'root' })
export class CacheService {
  private readonly authService = inject(AuthModuleService);

  private readonly store = new Map<string, CacheEntry>();

  constructor() {
    this.authService.sessionCleared$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.resetSession());
  }

  resetSession(): void {
    this.clear();
  }

  get<T>(key: string): T | null {
    const entry = this.store.get(key) as CacheEntry<T> | undefined;
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
      this.store.delete(key);
      return null;
    }
    return entry.data;
  }

  set<T>(key: string, data: T): void {
    if (this.store.size >= MAX_ENTRIES && !this.store.has(key)) {
      this.store.delete(this.store.keys().next().value as string);
    }
    this.store.set(key, { data, expiresAt: Date.now() + DEFAULT_TTL });
  }

  invalidateByPrefix(prefix: string): void {
    for (const key of this.store.keys()) {
      if (key.startsWith(prefix)) {
        this.store.delete(key);
      }
    }
  }

  clear(): void {
    this.store.clear();
  }
}
